import * as moment from "moment";
import { CloseIco, DatePickerIco } from "../../assets/customIcons";
import { DatePicker, DatePickerProps, ConfigProvider } from "antd";
import React, { FC, useEffect, useMemo, useState } from "react";

import { FormFieldWrapper } from "../index";
import classnames from "classnames";
import { default as localeRu } from "antd/es/date-picker/locale/ru_RU";
import ru_RU from "antd/lib/locale-provider/ru_RU";
import styles from "./IDatePicker.module.scss";
import { FieldError, FieldErrorsImpl, Merge } from "react-hook-form";
import {
  checkDateValue,
  getDisabledData,
} from "../../utils/helpers/data.helpers";
import { PickerLocale } from "antd/lib/date-picker/generatePicker";

const defaultMonths = {
  "January": "Январь",
  "February":"Февраль",
  "March": "Март",
  "April":"Апрель",
  "May":"Май",
  "June":"Июнь",
  "July":"Июль",
  "August":"Август",
  "September": "Сентябрь",
  "October":"Октябрь",
  "November": "Ноябрь",
  "December": "Декабрь"
}


const getDateValue = (
  fieldValue,
  format = "DD.MM.YYYY",
  value = fieldValue,
) => {
  const result = { currentValue: "", currentDate: null };
  if (checkDateValue(fieldValue)) {
    result.currentValue = moment(fieldValue).format(format);
    result.currentDate = moment(fieldValue);
  }
  if (!checkDateValue(fieldValue) && checkDateValue(value)) {
    result.currentValue = moment(value).format(format);
    result.currentDate = moment(value);
  }
  if (defaultMonths[result.currentValue]){
    result.currentValue = defaultMonths[result.currentValue]
  }
  return result;
};

interface IDatePickerProps {
  allowClear?: boolean;
  className?: string;
  placeholder?: string;
  label?: string;
  value?: moment.Moment;
  field?: any;
  disabled?: boolean;
  format?: string;
  picker?: "date" | "week" | "month" | "quarter" | "year";
  isRequired?: boolean;
  error?: string | FieldError | Merge<FieldError, FieldErrorsImpl<any>>;
  onChange?: (value: moment.Moment) => void;
  disableData?: unknown;
  superPrevIcon?: React.ReactNode;
  superNextIcon?: React.ReactNode;
  disabledHeader?: boolean;
  hideHeader?: boolean;
  defaultPickerValue?: moment.Moment;
  mode?: "time" | "date" | "month " | "year " | "decade";
  autocomplete?: string;
  locale?: PickerLocale;
}

export const IDatePicker: FC<IDatePickerProps> = ({
  allowClear = true,
  className,
  placeholder,
  label,
  field,
  disabled,
  isRequired,
  error,
  value,
  onChange,
  disabledHeader,
  defaultPickerValue,
  hideHeader,
  mode,
  format = "DD.MM.YYYY",
  picker = "date",
  disableData = getDisabledData,
  autocomplete = "off",
  locale = localeRu,
}) => {
  const [isEmpty, setIsEmpty] = useState(true);
  const [isFocus, setIsFocused] = useState(false);
  const noLabel = !placeholder && !label;

  const changePicker: DatePickerProps["onChange"] = (value, dateString) => {
    field?.onChange(getDateValue(value).currentDate);

    if (typeof onChange === "function") {
      onChange(getDateValue(value).currentDate);
    }
    setIsFocused(false);
  };

  useEffect(() => {
    if (field) {
      setIsEmpty(!field?.value);
    }
  }, [field?.value]);

  useEffect(() => {
    if (value) {
      setIsEmpty(!value);
    }
  }, [value]);

  const { currentValue, currentDate } = getDateValue(
    field?.value,
    format,
    value,
  );

  const getName: string = useMemo(() => {
    if (field?.name && isFocus && autocomplete !== "on") {
      return "";
    }
    return field?.name ?? "";
  }, [field, isFocus, currentValue]);

  const quarterFormatFn = (date) => {
    const q = date.format("Q") + "";
    return {
      "1": "1 кв.",
      "2": "2 кв.",
      "3": "3 кв.",
      "4": "4 кв.",
    }[q];
  };

  //@ts-ignore
  ru_RU.DatePicker.lang.quarterFormat = (date) => {
    return quarterFormatFn(date);
  };

  return (
    <FormFieldWrapper
      error={error}
      label={placeholder || label}
      value={currentValue}
      required={isRequired}
      disabled={disabled}
      isFocused={isFocus || !!currentValue}
      className={classnames(styles.datePicker, {
        [className]: className,
        [styles.noLabel]: noLabel,
      })}
      labelClassName={styles.label}
    >
      <ConfigProvider locale={ru_RU}>
        <DatePicker
          {...field}
          name={getName}
          autoComplete={autocomplete}
          mode={mode}
          popupClassName={classnames(styles.customDropdown, {
            [styles.disabledDropDown]: disabledHeader,
            [styles.hiddenDropDown]: hideHeader,
          })}
          defaultPickerValue={defaultPickerValue}
          allowClear={allowClear}
          disabled={disabled}
          disabledDate={disableData}
          onPanelChange={(e) => {
            // field.onChange(e);
          }}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            const key = event.key;
            if (key === "Backspace" || key === "Delete") {
              return;
            }
            const input = event.target as HTMLInputElement;
            const curValue = input.value;
            if (format == "DD.MM.YYYY") {
              //input.value = MASKED.resolve(input.value);

              if (curValue.length === 10) {
                event.preventDefault();
                return;
              }

              if (key < "0" || key > "9") {
                event.preventDefault();
                return;
              }

              let newValue = "";
              switch (curValue.length) {
                case 2:
                case 5:
                  newValue = curValue + ".";
                  break;
                default:
                  newValue = curValue;
              }

              input.value = newValue;
            }
            if (format == "YYYY") {
              if (curValue.length === 4) {
                event.preventDefault();
                return;
              }
              if (key < "0" || key > "9") {
                event.preventDefault();
                return;
              }
            }
          }}
          format={format}
          picker={picker}
          value={currentDate}
          placeholder={placeholder || label}
          //locale={locale}
          status={error && "error"}
          suffixIcon={<DatePickerIco />}
          clearIcon={<CloseIco />}
          onChange={changePicker}
          onFocus={(e) => {
            setIsFocused(true);
          }}
          onBlur={(e) => {
            field?.onBlur();
            setIsFocused(false);
          }}
          onOpenChange={setIsFocused}
        />
      </ConfigProvider>
    </FormFieldWrapper>
  );
};
